var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';
import Pagination from 'components/v2/PaginationV2';
import { isEqual, isEmpty, uniqBy, isString } from 'lodash';
import { humanize, unescapeHTML } from 'underscore.string';
import Spinner from 'components/ui/Spinner';
import { sluggify } from 'utils/localization/utils';
import { CARRIER_SUBSIDIARY_OPTIONS } from 'constants/subsidiary';
import { DRIVER_CATEGORIZATION_OPTIONS } from 'constants/DriverCategorization';
function getEventSource(source) {
    if (source.email === 'cargomatic-automation' ||
        source.email === 'cargomatic-automation@cargomatic.com') {
        return (React.createElement("img", { src: "/img/storm-trooper.jpg", alt: "cargomatic-automation", className: "icon", width: "25" }));
    }
    if (source._type === 'Driver') {
        if (!source.first_name)
            return source.email;
        if (source.first_name && source.first_name !== '') {
            return `${source.username} (${source.first_name} ${source.last_name})`;
        }
        return source.username;
    }
    return source.email;
}
const AuditTextToTranslate = [
    [...CARRIER_SUBSIDIARY_OPTIONS, ...DRIVER_CATEGORIZATION_OPTIONS].map((o) => o.value),
].flat();
class AuditHistory extends Component {
    constructor(props) {
        super(props);
        this.onPageSelect = (page) => {
            const _a = this.state, { audits, itemsPerPage } = _a, state = __rest(_a, ["audits", "itemsPerPage"]);
            let activePage = page;
            this.setState(Object.assign(Object.assign({}, state), { activeItems: audits.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage), activePage,
                itemsPerPage }));
        };
        this.getDate = (date) => {
            return moment(date).format('L');
        };
        let { itemsPerPage, paginate, keepDuplicates, audits = [] } = props;
        let total = audits.length;
        if (!Array.isArray(audits)) {
            total = audits === null || audits === void 0 ? void 0 : audits.totalCount;
        }
        if (!keepDuplicates) {
            audits = uniqBy(audits, ({ meta, event }) => JSON.stringify({ meta, event }));
        }
        const pages = audits ? Math.ceil(audits.length / itemsPerPage) : 1;
        let activeItems = audits;
        if (paginate) {
            activeItems = audits.slice(0, itemsPerPage);
        }
        this.state = {
            audits,
            activeItems,
            activePage: 1,
            pages,
            itemsPerPage,
            total,
        };
    }
    static getDerivedStateFromProps(nextProps, prevState) {
        let { audits: { items, totalCount }, keepDuplicates, } = nextProps;
        if (!items) {
            return null;
        }
        const { activeItems, itemsPerPage, activePage } = prevState;
        if (!keepDuplicates) {
            items = uniqBy(items, ({ meta, event }) => JSON.stringify({ meta, event }));
        }
        if (!isEqual(activeItems, items)) {
            return {
                activeItems: items.slice((activePage - 1) * itemsPerPage, activePage * itemsPerPage),
                total: totalCount,
            };
        }
        return null;
    }
    getEventLabel(event) {
        const { t } = this.props;
        const eventKey = sluggify(event);
        let label = t(eventKey);
        if (!label || label === eventKey) {
            label = humanize(event);
        }
        return label;
    }
    renderHistory() {
        if (!this.state.activeItems) {
            return null;
        }
        const { t, languageCodeId } = this.props;
        return this.state.activeItems.map((item, index) => {
            var _a, _b, _c, _d, _e;
            let event = this.getEventLabel(item.event);
            const timestamp = moment(item.timestamp).format('LLL');
            const source = getEventSource(item.user);
            let change = '';
            let shipment = '';
            let order = '';
            let link = '';
            let description = null;
            if (item.meta && item.meta.event_description) {
                description = item.meta.event_description;
            }
            if (item.meta && item.meta.value) {
                const value = item.meta.value;
                if (item.meta.shipment && typeof item.meta.shipment === 'string') {
                    shipment = item.meta.shipment;
                }
                if (item.meta.order && typeof item.meta.order === 'string') {
                    order = item.meta.order;
                }
                if (typeof value === 'string') {
                    if (!isEmpty(shipment) || !isEmpty(order)) {
                        const valueSplit = value.split(' ');
                        const linkId = valueSplit[valueSplit.length - 1];
                        valueSplit.pop();
                        const path = !isEmpty(shipment) ? 'shipments' : 'orders';
                        const id = !isEmpty(shipment) ? shipment : order;
                        change = valueSplit.join(' ');
                        link = (React.createElement("a", { href: `/${path}/${id}`, target: "_blank", rel: "noreferrer noopener" }, linkId));
                    }
                    else {
                        change = unescapeHTML(unescape(item.meta.value));
                    }
                }
            }
            let text;
            let title;
            if (change.length > 50) {
                text = change.substring(0, 40) + '...[truncated]';
                title = change;
            }
            else if (change && link) {
                text = `{change} {link}`;
                title = text;
            }
            else {
                text = change;
                title = change;
            }
            if (((_a = item === null || item === void 0 ? void 0 : item.meta) === null || _a === void 0 ? void 0 : _a.contractId) && ((_b = item === null || item === void 0 ? void 0 : item.meta) === null || _b === void 0 ? void 0 : _b.to)) {
                const { start_date: toStartDate = '', end_date: toEndDate = '' } = item.meta.to;
                if ((_c = item === null || item === void 0 ? void 0 : item.meta) === null || _c === void 0 ? void 0 : _c.from) {
                    const { start_date: fromStartDate = '', end_date: fromEndDate = '' } = item.meta.from;
                    change = t('START_DATE_END_DATE_CHANGED', {
                        startFrom: this.getDate(fromStartDate),
                        startTo: this.getDate(toStartDate),
                        endFrom: this.getDate(fromEndDate),
                        endTo: this.getDate(toEndDate),
                    });
                }
                if (!change) {
                    change = t('START_DATE_END_DATE_TIMELINE', {
                        startDate: this.getDate(toStartDate),
                        endDate: this.getDate(toEndDate),
                    });
                }
                text = change;
            }
            if ((_d = item === null || item === void 0 ? void 0 : item.meta) === null || _d === void 0 ? void 0 : _d.referenceNumberType) {
                return (React.createElement("tr", { key: index },
                    React.createElement("td", null,
                        React.createElement("div", { className: "text-xs break-all", title: event },
                            event,
                            React.createElement("div", { style: { marginTop: 16 } },
                                React.createElement("div", null,
                                    t('REFERENCE_NUMBER_TYPE'),
                                    ":",
                                    ' ',
                                    t(sluggify((_e = item === null || item === void 0 ? void 0 : item.meta) === null || _e === void 0 ? void 0 : _e.referenceNumberType))),
                                ['before', 'after'].map((el) => {
                                    var _a;
                                    if (!(el in item.meta))
                                        return null;
                                    return (React.createElement("div", { style: { marginTop: 4 }, key: el },
                                        t(el.toUpperCase()),
                                        ":",
                                        ' ',
                                        isString((_a = item === null || item === void 0 ? void 0 : item.meta) === null || _a === void 0 ? void 0 : _a[el])
                                            ? t(sluggify(item.meta[el]))
                                            : 'null'));
                                })))),
                    React.createElement("td", null,
                        React.createElement("div", { className: "w-20 text-xs", title: timestamp }, timestamp)),
                    React.createElement("td", null,
                        React.createElement("div", { className: "w-24 text-xs", title: source }, source))));
            }
            return (React.createElement("tr", { key: index },
                React.createElement("td", null,
                    React.createElement("div", { className: "text-xs break-all", title: event }, event),
                    !isEmpty(change) && (React.createElement("div", { style: { paddingTop: 20 }, className: "text-xs break-all text-grey-dark", title: title }, t('CHANGE_WITH_VALUE', {
                        text: AuditTextToTranslate.includes(text)
                            ? t(sluggify(text))
                            : text,
                    }))),
                    description && (React.createElement("div", { style: { paddingTop: 20 }, className: "text-xs break-all text-grey-dark", title: description }, description))),
                React.createElement("td", null,
                    React.createElement("div", { className: "w-20 text-xs", title: timestamp }, timestamp)),
                React.createElement("td", null,
                    React.createElement("div", { className: "w-24 text-xs", title: source }, source))));
        });
    }
    renderPagination() {
        const { t, isLoading, onClick, currentPage } = this.props;
        const { activePage, itemsPerPage, total } = this.state;
        const pagination = currentPage || activePage;
        let onPageClick = onClick || this.onPageSelect;
        return (React.createElement("div", { className: "flex justify-center my-2" }, isLoading ? (React.createElement(Spinner, { message: t('LOADING') })) : (React.createElement(Pagination, { perPage: itemsPerPage, page: pagination, total: total, onPageClick: onPageClick }))));
    }
    render() {
        const { t } = this.props;
        if (!this.props.audits ||
            (this.state.activeItems && this.state.activeItems.length === 0)) {
            return React.createElement("div", null,
                t('AWAITING_AUDIT_EVENTS'),
                "..");
        }
        return (React.createElement("div", { className: "overflow-x-auto" },
            React.createElement("table", { className: "default-table" },
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", null, t('NAME')),
                        React.createElement("th", null, t('TIMESTAMP')),
                        React.createElement("th", null, t('SOURCE')))),
                React.createElement("tbody", null, this.renderHistory())),
            this.props.paginate && this.renderPagination()));
    }
}
AuditHistory.propTypes = {
    audits: PropTypes.arrayOf(PropTypes.shape({
        meta: PropTypes.shape({
            value: PropTypes.any,
        }),
        event: PropTypes.string,
    })),
    paginate: PropTypes.bool,
    keepDuplicates: PropTypes.bool,
    itemsPerPage: PropTypes.number,
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    currentPage: PropTypes.number,
};
AuditHistory.defaultProps = {
    paginate: true,
    itemsPerPage: 10,
};
export default withTranslation()(AuditHistory);
